<template>
    <div ref="root" class="vfb-snippet" v-html="renderedHtml"></div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, nextTick } from 'vue';

// The snippet markup is rendered server-side (Pimcore `pimcore_inc`) and passed
// in via `renderedHtml`. We only output it here. The HTML is editor/server
// generated → v-html is acceptable.
interface Props {
    renderedHtml?: string;
}
const props = withDefaults(defineProps<Props>(), {
    renderedHtml: '',
});

const root = ref<HTMLElement | null>(null);

// The snippet HTML is injected after the client-side mount, so any page-level
// JS that scanned the DOM on load has already missed it. Inline <script> tags
// in v-html never execute either. We notify the host page so it can
// (re-)initialise its modules in this scope (project listens via initInScope).
// `<script>` inside the snippet must be loaded globally in the page root.
const notifyRendered = () => {
    const el = root.value;
    if (!el || !props.renderedHtml) return;
    el.dispatchEvent(new CustomEvent('vfb:snippet-rendered', {
        bubbles: true,
        detail: { el },
    }));
    // Convenience hook if the host exposes a global initialiser.
    try {
        (window as any).app?.initInScope?.(el);
    } catch { /* noop */ }
};

onMounted(() => nextTick(notifyRendered));
watch(() => props.renderedHtml, () => nextTick(notifyRendered));
</script>
